import { useRef, useState } from 'react';
import type { RefObject } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Squiggle from './Squiggle';
import { trackStandard, trackCustom, getUtmParams } from '../../lib/pixel';
import { trackPostHog } from '../../lib/analytics';

const POLE_OPTIONS = [
  'Médecine & Pharmacie',
  'Commerce & Gestion',
  'Ingénierie',
  'Pack Général + ENA',
  'Pas encore décidé',
] as const;

const QUI_REMPLIT_OPTIONS = ["L'étudiant(e)", 'Le parent'] as const;
type QuiRemplit = (typeof QUI_REMPLIT_OPTIONS)[number];

interface FormState {
  fullName: string;
  phone: string;
  email: string;
  pole: string;
  quiRemplit: QuiRemplit | '';
  message: string;
}

const INITIAL: FormState = {
  fullName: '',
  phone: '',
  email: '',
  pole: '',
  quiRemplit: '',
  message: '',
};

const validatePhone = (phone: string): boolean => {
  const cleaned = phone.replace(/[\s\-().]/g, '');
  return /^(\+212|0)(5|6|7)\d{8}$/.test(cleaned);
};

const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Check = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PostbacForm = () => {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formStarted = useRef(false);
  const fullNameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackPostHog('form_started', { page: 'prepa-post-bac' });
  };

  const focusNext = (ref: RefObject<HTMLInputElement>) => {
    setTimeout(() => ref.current?.focus(), 50);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (form.fullName.trim().length < 2) next.fullName = 'Au moins 2 caractères';
    if (!form.phone.trim()) next.phone = 'Téléphone requis';
    else if (!validatePhone(form.phone)) next.phone = 'Numéro marocain invalide (ex. 06 12 34 56 78)';
    if (form.email.trim() && !validateEmail(form.email)) next.email = 'Format email invalide';
    if (!form.pole) next.pole = 'Choisissez un pôle';
    if (!form.quiRemplit) next.quiRemplit = 'Indiquez qui remplit';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const emailTrimmed = form.email.trim();
      const messageTrimmed = form.message.trim();
      await axios.post('/api/postbac', {
        full_name: form.fullName.trim(),
        phone: form.phone.trim(),
        pole: form.pole,
        qui_remplit: form.quiRemplit,
        ...(emailTrimmed ? { email: emailTrimmed } : {}),
        ...(messageTrimmed ? { message: messageTrimmed } : {}),
        ...getUtmParams(),
      });

      trackStandard('Lead', { content_name: 'postbac', currency: 'MAD' });
      trackCustom('postbac_submitted', {
        pole: form.pole,
        ...getUtmParams(),
      });
      trackPostHog('form_submitted', {
        page: 'prepa-post-bac',
        pole: form.pole,
        has_email: Boolean(emailTrimmed),
      });

      setIsSubmitted(true);
    } catch (err) {
      console.error('Postbac submission error:', err);
      setSubmitError(
        'Une erreur est survenue. Réessayez ou appelez-nous au 05 22 93 04 08.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section className="ppb-form-section" id="form">
        <div className="container">
          <div className="ppb-confirm">
            <div className="ppb-confirm-check" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="ppb-confirm-title">Merci, votre demande est bien reçue.</h2>
            <p className="ppb-confirm-body">
              Un conseiller orientation vous recontacte prochainement pour vous
              présenter le programme détaillé de la prépa.
            </p>
            <a
              href="https://gssanaa.com"
              target="_blank"
              rel="noopener noreferrer"
              className="ppb-confirm-link"
            >
              En attendant, découvrez GS Sanaa sur gssanaa.com →
            </a>
          </div>
        </div>
      </section>
    );
  }

  const inputCls = (k: keyof FormState, base: string) =>
    `${base}${errors[k] ? ' has-error' : ''}`;

  return (
    <section className="ppb-form-section" id="form">
      <div className="container ppb-form-grid">
        <div className="ppb-form-aside">
          <p className="ppb-eyebrow">Recevez le programme</p>
          <h2 className="ppb-form-title">
            Parlons de votre{' '}
            <span className="ppb-form-title-emph">
              orientation.
              <Squiggle className="ppb-hero-squiggle" color="var(--gold)" />
            </span>
          </h2>
          <p className="ppb-form-aside-body">
            Laissez-nous vos coordonnées et le pôle qui vous intéresse.
            Nous revenons vers vous rapidement.
          </p>
          <ul className="ppb-checklist">
            <li>
              <span className="ppb-check"><Check /></span>
              Le programme détaillé de la prépa
            </li>
            <li>
              <span className="ppb-check"><Check /></span>
              Un échange avec un conseiller orientation
            </li>
          </ul>
        </div>

        <form className="ppb-form" onSubmit={handleSubmit} noValidate>
          {submitError && (
            <p className="ppb-form-error" role="alert">
              {submitError}
            </p>
          )}

          <div className="ppb-field">
            <label htmlFor="ppb-fullName" className="ppb-field-label">
              Nom complet
            </label>
            <input
              ref={fullNameRef}
              id="ppb-fullName"
              type="text"
              value={form.fullName}
              onChange={(e) => setField('fullName', e.target.value)}
              onFocus={handleFocus}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  phoneRef.current?.focus();
                }
              }}
              placeholder="Yasmine El Amrani"
              className={inputCls('fullName', 'ppb-input')}
              autoComplete="name"
              enterKeyHint="next"
            />
            {errors.fullName && <p className="ppb-field-error">{errors.fullName}</p>}
          </div>

          <div className="ppb-field-row">
            <div className="ppb-field">
              <label htmlFor="ppb-phone" className="ppb-field-label">
                Téléphone
              </label>
              <input
                ref={phoneRef}
                id="ppb-phone"
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                onFocus={handleFocus}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    emailRef.current?.focus();
                  }
                }}
                placeholder="06 12 34 56 78"
                className={inputCls('phone', 'ppb-input')}
                autoComplete="tel"
                enterKeyHint="next"
              />
              {errors.phone && <p className="ppb-field-error">{errors.phone}</p>}
            </div>
            <div className="ppb-field">
              <label htmlFor="ppb-email" className="ppb-field-label">
                Email <span className="ppb-field-optional">(optionnel)</span>
              </label>
              <input
                ref={emailRef}
                id="ppb-email"
                type="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                onFocus={handleFocus}
                placeholder="vous@email.com"
                className={inputCls('email', 'ppb-input')}
                autoComplete="email"
                enterKeyHint="next"
              />
              {errors.email && <p className="ppb-field-error">{errors.email}</p>}
            </div>
          </div>

          <div className="ppb-field">
            <label htmlFor="ppb-pole" className="ppb-field-label">
              Le pôle qui vous intéresse
            </label>
            <select
              id="ppb-pole"
              value={form.pole}
              onChange={(e) => setField('pole', e.target.value)}
              onFocus={handleFocus}
              className={inputCls('pole', 'ppb-select')}
            >
              <option value="" disabled>Choisissez un pôle…</option>
              {POLE_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            {errors.pole && <p className="ppb-field-error">{errors.pole}</p>}
          </div>

          <div className="ppb-field">
            <span className="ppb-field-label">Vous êtes</span>
            <div className="ppb-radio-grid">
              {QUI_REMPLIT_OPTIONS.map((q) => {
                const active = form.quiRemplit === q;
                return (
                  <button
                    type="button"
                    key={q}
                    className={`ppb-radio-btn${active ? ' is-active' : ''}`}
                    onClick={() => {
                      handleFocus();
                      setField('quiRemplit', q);
                      if (!form.fullName) focusNext(fullNameRef);
                    }}
                    aria-pressed={active}
                  >
                    {q}
                    <span className="ppb-radio-check" aria-hidden="true"><Check /></span>
                  </button>
                );
              })}
            </div>
            {errors.quiRemplit && <p className="ppb-field-error">{errors.quiRemplit}</p>}
          </div>

          <div className="ppb-field">
            <label htmlFor="ppb-message" className="ppb-field-label">
              Message <span className="ppb-field-optional">(optionnel)</span>
            </label>
            <textarea
              id="ppb-message"
              value={form.message}
              onChange={(e) => setField('message', e.target.value)}
              onFocus={handleFocus}
              placeholder="Une question, un besoin particulier…"
              className="ppb-textarea"
              rows={3}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary btn-lg btn-block"
          >
            {isSubmitting ? (
              <>
                <span className="ppb-spinner" aria-hidden="true" />
                Envoi en cours…
              </>
            ) : (
              <>
                Recevoir le programme
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>

          <p className="ppb-form-legal">
            En envoyant ce formulaire, vous acceptez d'être recontacté par GS Sanaa.
            Voir notre{' '}
            <Link to="/politique-confidentialite">politique de confidentialité</Link>.
          </p>
        </form>
      </div>
    </section>
  );
};

export default PostbacForm;
